import React, {FC, ReactNode} from 'react';
import styled, {css} from 'styled-components';

import {PaletteColorsEnum} from '../../../../libs/shared/core/src/models/api/paletteModel';
import {ButtonVariantsEnum} from '../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {ButtonContent} from '../../../../libs/shared/ui/src/components/buttons/buttonContent';
import {ChromelessButton} from '../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {IconButton} from '../../../../libs/shared/ui/src/components/buttons/iconButton';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';

interface ErrorsBannerProps {
  description: ReactNode;
  buttonContent: ReactNode;
  onButtonClick: () => void;
  isDisabled?: boolean;
  onCloseClick?: () => void;
  className?: string;
}

/*
 * Style.
 */

interface StyledBannerWrapperDivProps {
  $isDisabled: boolean;
}

const StyledBannerWrapperDiv = styled.div<StyledBannerWrapperDivProps>`
  display: flex;
  flex-flow: row;
  flex-wrap: wrap;
  gap: 10px;
  background: ${(p) => p.theme.palette.orange.shade20};
  border-radius: 8px;
  padding: 9px 12px;
  ${(p) =>
    p.$isDisabled &&
    css`
      opacity: 0.4;
      pointer-events: none;
    `}
`;

const StyledChromelessButton = styled(ChromelessButton)`
  margin: -5px -6px -5px 0;
`;

const StyledButtonContent = styled(ButtonContent)`
  font-size: ${fontSizes.tiny};
  white-space: nowrap;
  border-radius: 13px;
`;

const StyledTextWrapperDiv = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  line-height: 16px;
  font-size: ${fontSizes.verySmall};
  font-weight: ${fontWeights.medium};
`;

const StyledIconButton = styled(IconButton)`
  margin: -5px -6px -5px 0;
  border-radius: 50%;
`;

export const ErrorBanner: FC<ErrorsBannerProps> = (props) => {
  const theme = useUserTheme();

  return (
    <StyledBannerWrapperDiv className={props.className} $isDisabled={Boolean(props.isDisabled)}>
      <Icon name="warningFilledSmall" color={theme.palette.orange.shade50} />
      <StyledTextWrapperDiv>{props.description}</StyledTextWrapperDiv>
      <StyledChromelessButton onClick={props.onButtonClick}>
        <StyledButtonContent
          size={VisualSizesEnum.SMALL}
          variant={ButtonVariantsEnum.LINK}
          color={PaletteColorsEnum.BLUE}
        >
          {props.buttonContent}
        </StyledButtonContent>
      </StyledChromelessButton>
      {maybeRenderCloseButton(props.onCloseClick)}
    </StyledBannerWrapperDiv>
  );
};

function maybeRenderCloseButton(onCloseClick?: () => void) {
  if (!onCloseClick) {
    return null;
  }
  return <StyledIconButton iconName="closeSmall" onClick={onCloseClick} label={null} />;
}
